import { useToast } from "primevue/usetoast";
import { getUserErrorMessage } from "@/utils/errorHandler";
import { logger } from "@/utils/logger";
import { captureException, isSentryAvailable } from "@/modules/sentry";

export function useErrorHandler() {
  const toast = useToast();

  function extractErrorMessage(error: unknown): string {
    if (!error) {
      return "An unexpected error occurred";
    }

    if (typeof error === "string") {
      return error;
    }

    return getUserErrorMessage(error);
  }

  interface ErrorHandlerOptions {
    silent?: boolean;
    life?: number;
    severity?: "error" | "warn" | "info" | "success";
  }

  function handleError(
    error: unknown,
    context = "Error",
    options: ErrorHandlerOptions = {},
  ) {
    const { silent = false, life = 5000, severity = "error" } = options;

    const message = extractErrorMessage(error);

    if (!silent) {
      toast.add({
        severity,
        summary: context,
        detail: message,
        life,
        closable: true,
      });
    }

    if (import.meta.env.DEV) {
      console.error(`[${context}]`, error);
      logger.error(`[${context}]`, error);
    }

    const alreadyCaptured =
      error &&
      typeof error === "object" &&
      (error as { _sentryCaptured?: boolean })._sentryCaptured;

    if (isSentryAvailable() && !alreadyCaptured) {
      try {
        captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: {
            context,
          },
          extra: {
            message,
            userMessage: message,
          },
        });
      } catch (sentryError) {
        if (import.meta.env.DEV) {
          logger.warn("Failed to send error to Sentry:", sentryError);
        }
      }
    }
  }

  function handleSuccess(message: string, summary = "Success", life = 3000) {
    toast.add({
      severity: "success",
      summary,
      detail: message,
      life,
      closable: true,
    });
  }

  function handleWarning(message: string, summary = "Warning", life = 4000) {
    toast.add({
      severity: "warn",
      summary,
      detail: message,
      life,
      closable: true,
    });
  }

  function handleInfo(message: string, summary = "Info", life = 3000) {
    toast.add({
      severity: "info",
      summary,
      detail: message,
      life,
      closable: true,
    });
  }

  return {
    handleError,
    handleSuccess,
    handleWarning,
    handleInfo,
    extractErrorMessage,
  };
}
